import { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, CombinedTable, Reservation, TableAvailability, TableWeekdayAvailability } from "@/types/table";
import { Users, Layers, CheckCircle2, Clock, Armchair } from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";
import { cn } from "@/lib/utils";
import { isTableAvailableOnDate } from "@/lib/tableAvailabilityUtils";
import { getBlockedTableIds, ConflictCheckOptions } from "@/lib/reservationConflictUtils";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { format } from "date-fns";
import { useServiceTimeCompare } from "@/hooks/useServiceTimeCompare";
import { useServiceSlots } from "@/hooks/useServiceTimeCompare";

interface WaitlistReservationInfo {
  id: number;
  supabaseId: string;
  name: string;
  persons: number;
  numberOfChildren: number;
  hasStroller?: boolean;
  time: string;
  reservationDate: Date;
}

interface WaitlistAssignTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reservation: WaitlistReservationInfo | null;
  tables: Table[];
  combinedTables: CombinedTable[];
  allReservations: Reservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  onAssign: (reservationId: number, tableId: string | null, combinedTableId: string | null, newStatus: "en_attente" | "arrivé") => void;
  reservationSettings?: {
    bookingMode: "slot" | "time";
    reservationDurationMinutes: number;
  };
}

export function WaitlistAssignTableDialog({
  open,
  onOpenChange,
  reservation,
  tables,
  combinedTables,
  allReservations,
  availabilities,
  weekdayAvailabilities,
  onAssign,
  reservationSettings,
}: WaitlistAssignTableDialogProps) {
  const compareServiceTime = useServiceTimeCompare();
  const serviceSlots = useServiceSlots();
  const dateStr = reservation ? format(reservation.reservationDate, "yyyy-MM-dd") : "";

  // Get reservations for this date (excluding cancelled and en_attente)
  const dateReservations = useMemo(() => {
    return allReservations.filter(r => {
      const rDate = r.date instanceof Date ? formatDateLocal(r.date) : String(r.date);
      return rDate === dateStr && r.status !== "annulé" && r.status !== "en_attente";
    });
  }, [allReservations, dateStr]);

  // Build blocked table IDs for this time slot
  const blockedTableIds = useMemo(() => {
    if (!reservation) return new Set<string>();
    const conflictOptions: ConflictCheckOptions | undefined = reservationSettings
      ? { bookingMode: reservationSettings.bookingMode, reservationDurationMinutes: reservationSettings.reservationDurationMinutes, serviceSlots }
      : undefined;
    return getBlockedTableIds(allReservations, combinedTables, reservation.reservationDate, reservation.time, conflictOptions);
  }, [allReservations, combinedTables, reservation, reservationSettings, serviceSlots]);

  // Build table list with reservations
  const tableItems = useMemo(() => {
    if (!reservation) return [];
    return tables
      .filter(t => t.isActive)
      .map(table => {
        const isFree = !blockedTableIds.has(table.id);
        const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === table.id) || null;
        const isDateAvailable = isTableAvailableOnDate(table, reservation.reservationDate, availabilities, weekdayAvail);
        const tableReservations = dateReservations
          .filter(r => r.tableId === table.id)
          .sort((a, b) => compareServiceTime(a.startTime, b.startTime));
        return { type: "table" as const, id: table.id, name: table.name, capacity: table.capacity, isFree: isFree && isDateAvailable, reservations: tableReservations, isDateAvailable };
      })
      .sort((a, b) => {
        // Free first, then by capacity match
        if (a.isFree && !b.isFree) return -1;
        if (!a.isFree && b.isFree) return 1;
        const diffA = Math.abs(a.capacity - reservation.persons);
        const diffB = Math.abs(b.capacity - reservation.persons);
        return diffA - diffB;
      });
  }, [tables, blockedTableIds, weekdayAvailabilities, availabilities, dateReservations, reservation, compareServiceTime]);

  const combinedItems = useMemo(() => {
    if (!reservation) return [];
    return combinedTables
      .filter(ct => ct.isActive)
      .map(ct => {
        const isFree = !blockedTableIds.has(ct.id) && !ct.tableIds.some(tid => blockedTableIds.has(tid));
        const allAvailable = ct.tableIds.every(tableId => {
          const table = tables.find(t => t.id === tableId);
          if (!table) return false;
          const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tableId) || null;
          return isTableAvailableOnDate(table, reservation.reservationDate, availabilities, weekdayAvail);
        });
        const ctReservations = dateReservations
          .filter(r => r.combinedTableId === ct.id)
          .sort((a, b) => compareServiceTime(a.startTime, b.startTime));
        return { type: "combined" as const, id: ct.id, name: ct.name, capacity: ct.totalCapacity, isFree: isFree && allAvailable, reservations: ctReservations, isDateAvailable: allAvailable };
      })
      .sort((a, b) => {
        if (a.isFree && !b.isFree) return -1;
        if (!a.isFree && b.isFree) return 1;
        const diffA = Math.abs(a.capacity - reservation.persons);
        const diffB = Math.abs(b.capacity - reservation.persons);
        return diffA - diffB;
      });
  }, [combinedTables, tables, blockedTableIds, weekdayAvailabilities, availabilities, dateReservations, reservation, compareServiceTime]);

  if (!reservation) return null;

  const getCapacityBadge = (capacity: number) => {
    const diff = capacity - reservation.persons;
    if (diff === 0) return <Badge className="bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30 text-[10px]">Parfait</Badge>;
    if (diff === 1) return <Badge className="bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30 text-[10px]">+1</Badge>;
    if (diff > 1) return <Badge variant="secondary" className="text-[10px]">+{diff}</Badge>;
    return <Badge variant="destructive" className="text-[10px]">{diff}</Badge>;
  };

  const renderTableCard = (item: { type: "table" | "combined"; id: string; name: string; capacity: number; isFree: boolean; reservations: Reservation[]; isDateAvailable: boolean }) => (
    <div
      key={item.id}
      className={cn(
        "rounded-lg border p-3 space-y-2",
        item.isFree ? "border-green-500/30 bg-green-50/50 dark:bg-green-950/10" : "border-border bg-card"
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{item.name}</span>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Users className="w-3 h-3" />
            {item.capacity}
          </span>
          {getCapacityBadge(item.capacity)}
        </div>
        {item.isFree ? (
          <Badge variant="outline" className="text-[10px] border-green-500/50 text-green-700 dark:text-green-400">
            <CheckCircle2 className="w-3 h-3 mr-0.5" />
            Libre
          </Badge>
        ) : !item.isDateAvailable ? (
          <Badge variant="outline" className="text-[10px] text-muted-foreground">Indisponible</Badge>
        ) : null}
      </div>

      {/* Existing reservations on this table */}
      {item.reservations.length > 0 && (
        <div className="space-y-1">
          {item.reservations.map(r => (
            <div key={r.id} className="flex items-center gap-2 text-xs text-muted-foreground pl-1">
              <Clock className="w-3 h-3 shrink-0" />
              <span>{r.startTime.substring(0, 5)}</span>
              <span className="truncate">{r.customerFirstName} {r.customerLastName}</span>
              <span className="shrink-0">({r.numberOfPeople}p)</span>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs flex-1"
          onClick={() => {
            onAssign(reservation.id, item.type === "table" ? item.id : null, item.type === "combined" ? item.id : null, "en_attente");
            onOpenChange(false);
          }}
        >
          <Armchair className="w-3 h-3 mr-1" />
          Assigner
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs flex-1"
          onClick={() => {
            onAssign(reservation.id, item.type === "table" ? item.id : null, item.type === "combined" ? item.id : null, "arrivé");
            onOpenChange(false);
          }}
        >
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Assigner + Arrivé
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-base">Assigner une table</DialogTitle>
          <DialogDescription className="text-sm flex items-center gap-1.5 flex-wrap">
            <span>{reservation.name} • {reservation.persons} personne{reservation.persons > 1 ? "s" : ""}{reservation.numberOfChildren > 0 ? ` + ${reservation.numberOfChildren} enfant${reservation.numberOfChildren > 1 ? "s" : ""}` : ""}</span>
            {reservation.hasStroller && <StrollerIcon size={14} className="text-muted-foreground" />}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="px-4 pb-4 space-y-4">
            {/* Individual tables */}
            {tableItems.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tables individuelles</p>
                {tableItems.map(renderTableCard)}
              </div>
            )}

            {/* Combined tables */}
            {combinedItems.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <Layers className="w-3 h-3" />
                  Tables combinées
                </p>
                {combinedItems.map(renderTableCard)}
              </div>
            )}

            {tableItems.length === 0 && combinedItems.length === 0 && (
              <div className="py-8 text-center text-muted-foreground">
                <p className="text-sm">Aucune table active</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
